import {
  createFileRoute,
  Link,
  Outlet,
  useChildMatches,
  useNavigate,
} from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  ArrowLeft,
  FileText,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Circle,
  MinusCircle,
  ShoppingCart,
  Send,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth, SessionExpiredError } from "@/lib/auth-client";
import type {
  PurchaseRequestDetailDto,
  StepStatus,
  AuditActionType,
  VendorDto,
} from "@procureflow/shared-types";
import { Attachments } from "@/components/attachments";
import { LineItemsTable } from "@/components/line-items-table";
import { TransactionStatusBadge } from "@/components/transaction-status-badge";
import { PR_STATUS_CONFIG } from "@/lib/status-config";
import { formatAmount, formatDate, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/purchase-requests/$id")({
  head: () => ({
    meta: [{ title: "Purchase Request Detail — ProcureFlow" }],
  }),
  component: function PRDetailOrOutlet() {
    // The detail route parents the nested /edit route. Render the child
    // (edit form) when one is matched; otherwise render the detail page.
    const child = useChildMatches();
    return child.length > 0 ? <Outlet /> : <PRDetailPage />;
  },
});

// ── Timeline ──────────────────────────────────────────────────────────────────

const TIMELINE_LABELS: Partial<Record<AuditActionType, string>> = {
  PR_CREATED: "Request Created",
  PR_SUBMITTED: "Submitted for Approval",
  APPROVAL_GRANTED: "Approved",
  APPROVAL_REJECTED: "Rejected",
  APPROVAL_CHANGES_REQUESTED: "Changes Requested",
  PR_APPROVED: "Request Approved",
  PR_REJECTED: "Request Rejected",
  PR_CHANGES_REQUESTED: "Changes Requested",
};

type TimelineItemStyle = { icon: React.ReactNode; dotClass: string };

function timelineStyle(action: AuditActionType): TimelineItemStyle {
  switch (action) {
    case "PR_APPROVED":
    case "APPROVAL_GRANTED":
      return {
        icon: <CheckCircle2 className="h-4 w-4 text-[var(--success)]" />,
        dotClass: "bg-[var(--success)]",
      };
    case "PR_REJECTED":
    case "APPROVAL_REJECTED":
      return {
        icon: <XCircle className="h-4 w-4 text-destructive" />,
        dotClass: "bg-destructive",
      };
    case "APPROVAL_CHANGES_REQUESTED":
    case "PR_CHANGES_REQUESTED":
      return {
        icon: <AlertCircle className="h-4 w-4 text-[var(--warning)]" />,
        dotClass: "bg-[var(--warning)]",
      };
    case "PR_SUBMITTED":
      return {
        icon: <Clock className="h-4 w-4 [color:var(--info)]" />,
        dotClass: "bg-[var(--info)]",
      };
    default:
      return {
        icon: <Circle className="h-4 w-4 text-muted-foreground" />,
        dotClass: "bg-muted-foreground",
      };
  }
}

// ── Stepper ───────────────────────────────────────────────────────────────────

type StepIconProps = { status: StepStatus; isCurrent: boolean };

function StepIcon({ status, isCurrent }: StepIconProps) {
  if (status === "Approved")
    return <CheckCircle2 className="h-5 w-5 text-[var(--success)] shrink-0" />;
  if (status === "Rejected") return <XCircle className="h-5 w-5 text-destructive shrink-0" />;
  if (status === "ChangesRequested")
    return <AlertCircle className="h-5 w-5 text-[var(--warning)] shrink-0" />;
  if (status === "Skipped")
    return <MinusCircle className="h-5 w-5 text-muted-foreground shrink-0" />;
  return (
    <div
      className={`h-5 w-5 rounded-full border-2 shrink-0 ${
        isCurrent ? "border-primary bg-primary/20" : "border-muted-foreground/40 bg-muted"
      }`}
    />
  );
}

function stepStatusLabel(status: StepStatus, isCurrent: boolean): string {
  if (status === "Pending") return isCurrent ? "Awaiting action" : "Blocked";
  const map: Partial<Record<StepStatus, string>> = {
    Approved: "Approved",
    Rejected: "Rejected",
    ChangesRequested: "Changes Requested",
    Skipped: "Skipped",
    Escalated: "Escalated",
  };
  return map[status] ?? status;
}

type ActionKind = "approve" | "reject" | "request-changes";

const ACTION_LABELS: Record<ActionKind, string> = {
  approve: "Approve",
  reject: "Reject",
  "request-changes": "Request Changes",
};

// ── Page component ────────────────────────────────────────────────────────────

function PRDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [detail, setDetail] = useState<PurchaseRequestDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Approver action state
  const [pendingAction, setPendingAction] = useState<{ stepId: string; kind: ActionKind } | null>(
    null,
  );
  const [actionComment, setActionComment] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // draft delete (PR root document) Dialog state.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // US-063 close-PR (cancel remaining) state.
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  // US-023 generate-PO panel state.
  const [genPoOpen, setGenPoOpen] = useState(false);
  const [genPoVendors, setGenPoVendors] = useState<VendorDto[]>([]);
  const [genPoVendorId, setGenPoVendorId] = useState<string>("");
  const [genPoLoading, setGenPoLoading] = useState(false);
  const [genPoError, setGenPoError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchDetail() {
      setLoading(true);
      setError(null);
      setErrorStatus(null);
      try {
        const res = await fetchWithAuth(`/api/purchase-requests/${id}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) {
            setErrorStatus(res.status);
            setError(body.message ?? "Failed to load purchase request.");
          }
          return;
        }
        const { data } = await res.json();
        if (!cancelled) setDetail(data);
      } catch (err) {
        // SessionExpiredError → AuthContext handles redirect; don't paint a
        // local error banner during the navigation window.
        if (err instanceof SessionExpiredError) return;
        if (!cancelled) setError("An unexpected error occurred.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function refetchDetail() {
    try {
      const res = await fetchWithAuth(`/api/purchase-requests/${id}`);
      if (!res.ok) return;
      const { data } = await res.json();
      setDetail(data);
    } catch {
      // silent — existing detail stays visible
    }
  }

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetchWithAuth(`/api/purchase-requests/${id}/submit`, {
        method: "POST",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setSubmitError(body.message ?? "Failed to submit.");
        return;
      }
      await refetchDetail();
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAction() {
    if (!pendingAction) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const body: Record<string, string> = {};
      if (actionComment.trim()) body.comment = actionComment.trim();
      const res = await fetchWithAuth(
        `/api/approval-steps/${pendingAction.stepId}/${pendingAction.kind}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        const respBody = await res.json().catch(() => ({}));
        setActionError(respBody.message ?? "Action failed. Please try again.");
        return;
      }
      setPendingAction(null);
      setActionComment("");
      await refetchDetail();
    } finally {
      setActionLoading(false);
    }
  }

  // ── Loading / error states ──────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Link
          to="/purchase-requests"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Purchase Requests
        </Link>
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2">
          {errorStatus === 403 ? (
            <>
              <p className="text-sm font-medium text-foreground">Access denied</p>
              <p className="text-sm text-muted-foreground">
                You do not have permission to view this purchase request.
              </p>
            </>
          ) : errorStatus === 404 ? (
            <>
              <p className="text-sm font-medium text-foreground">Not found</p>
              <p className="text-sm text-muted-foreground">
                This purchase request does not exist or has been removed.
              </p>
            </>
          ) : (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>
      </div>
    );
  }

  // Active step = lowest Pending level (all lower levels Approved by definition of ordering)
  const firstPendingLevel = detail.approval_steps.find((s) => s.status === "Pending")?.level ?? -1;
  const activeStep =
    firstPendingLevel >= 0
      ? (detail.approval_steps.find(
          (s) => s.status === "Pending" && s.level === firstPendingLevel,
        ) ?? null)
      : null;
  const canAct = activeStep !== null && user?.id === activeStep.assigned_to.id;

  const canSubmit =
    user?.role === "Requester" &&
    user.id === detail.requester.id &&
    (detail.status === "Draft" || detail.status === "ChangesRequested");

  // Edit is available to the creator while the PR is still mutable (Draft or
  // ChangesRequested). Mirrors the PUT /api/purchase-requests/:id gate.
  const canEdit =
    user?.id === detail.requester.id &&
    (detail.status === "Draft" || detail.status === "ChangesRequested");

  // draft delete — the creator can delete their own Draft PR (the root
  // document). Mirrors the DELETE /api/purchase-requests/:id guard
  // (Requester-only, owner-scoped, Draft-only).
  const canDelete =
    user?.role === "Requester" && user.id === detail.requester.id && detail.status === "Draft";

  // US-023: Generate PO from an Approved PR. Backend restricts the
  // action to ProcurementOfficer; PO_EXISTS rejection stays server-side
  // (a defensive duplicate-PO check would need an extra round-trip).
  const canGeneratePo = user?.role === "ProcurementOfficer" && detail.status === "Approved";

  // US-018: Create RFQ from an Approved PR. Same RBAC + status gate as
  // Generate PO above. CTA navigates to /rfq/new?prId=<pr.id>; the form
  // there fetches the PR (for line-item pre-population) and active vendors.
  // Note: the reverse PR→RFQ link is DEFERRED per US-018 Notes — that's
  // a separate contract-coordinated change.
  const canCreateRfq = user?.role === "ProcurementOfficer" && detail.status === "Approved";

  // US-063: Close a PR by cancelling its remaining (un-consumed) quantity
  // without raising an RFQ. Available to ProcurementOfficer/Admin while the PR
  // is still consumable (Approved / PartiallyConsumed).
  const canClosePr =
    (user?.role === "ProcurementOfficer" || user?.role === "Admin") &&
    (detail.status === "Approved" || detail.status === "PartiallyConsumed");

  async function handleClosePr() {
    setClosing(true);
    setCloseError(null);
    try {
      const res = await fetchWithAuth(`/api/purchase-requests/${detail!.id}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(closeReason.trim() ? { reason: closeReason.trim() } : {}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setCloseError(body.message ?? "Failed to close the purchase request.");
        return;
      }
      const { data } = (await res.json()) as { data: PurchaseRequestDetailDto };
      setDetail(data);
      setCloseOpen(false);
      setCloseReason("");
    } finally {
      setClosing(false);
    }
  }

  // draft delete — DELETE the Draft PR (root document; nothing to release).
  // On success, toast + navigate back to the list. The now-deleted PR is gone
  // from the store so the list won't show it.
  async function handleDelete() {
    setDeleting(true);
    try {
      const res = await fetchWithAuth(`/api/purchase-requests/${detail!.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body?.message ?? "Failed to delete purchase request.");
        return;
      }
      setDeleteOpen(false);
      toast.success("Purchase request deleted.");
      navigate({ to: "/purchase-requests" });
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      toast.error("An unexpected error occurred.");
    } finally {
      setDeleting(false);
    }
  }

  async function openGenPo() {
    setGenPoOpen(true);
    setGenPoVendorId("");
    setGenPoError(null);
    if (genPoVendors.length > 0) return;
    try {
      const res = await fetchWithAuth(`/api/vendors?limit=100`);
      if (!res.ok) {
        setGenPoError("Failed to load vendors.");
        return;
      }
      const { data } = (await res.json()) as { data: VendorDto[] };
      // Only Active vendors are eligible per backend INVALID_VENDOR gate.
      setGenPoVendors(data.filter((v) => v.status === "Active"));
    } catch {
      setGenPoError("Failed to load vendors.");
    }
  }

  async function submitGenPo() {
    if (!genPoVendorId) return;
    setGenPoLoading(true);
    setGenPoError(null);
    try {
      const res = await fetchWithAuth(`/api/purchase-requests/${detail!.id}/generate-po`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vendor_id: genPoVendorId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setGenPoError(body.message ?? "PO generation failed.");
        return;
      }
      const { data } = (await res.json()) as { data: { id: string } };
      navigate({ to: "/purchase-orders/$id", params: { id: data.id } });
    } finally {
      setGenPoLoading(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Back link */}
      <Link
        to="/purchase-requests"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Purchase Requests
      </Link>

      {/* Header card */}
      <div className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="space-y-2 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-primary/10 grid place-items-center text-primary shrink-0">
                  <FileText className="h-3.5 w-3.5" />
                </div>
                <span className="font-mono text-sm font-medium text-muted-foreground">
                  {detail.pr_number}
                </span>
              </div>
              <TransactionStatusBadge config={PR_STATUS_CONFIG[detail.status]} />
            </div>
            <h1 className="text-lg font-semibold tracking-tight leading-snug">{detail.title}</h1>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
              <span>
                <span className="text-foreground font-medium">Cost Centre:</span>{" "}
                {detail.cost_centre}
              </span>
              <span>
                <span className="text-foreground font-medium">Required By:</span>{" "}
                {formatDate(detail.required_by_date)}
              </span>
              <span>
                <span className="text-foreground font-medium">Requested By:</span>{" "}
                {detail.requester.name}
              </span>
              <span>
                <span className="text-foreground font-medium">Created:</span>{" "}
                {formatDateTime(detail.created_at)}
              </span>
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-2xl font-bold tabular-nums tracking-tight">
              {formatAmount(detail.total_amount)}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {detail.line_items.length} line{detail.line_items.length !== 1 ? "s" : ""}
            </div>
            {canEdit && (
              <div className="mt-3">
                <Link
                  to="/purchase-requests/$id/edit"
                  params={{ id: detail.id }}
                  data-testid="edit-pr-button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted transition"
                >
                  Edit
                </Link>
              </div>
            )}
            {canDelete && (
              <div className="mt-3">
                <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="delete-pr-button">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </Button>
                  </DialogTrigger>
                  <DialogContent dismissable={false}>
                    <DialogHeader>
                      <DialogTitle>Delete this draft purchase request?</DialogTitle>
                      <DialogDescription>
                        This permanently deletes the purchase request and its line items. This can't
                        be undone.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setDeleteOpen(false)}
                        disabled={deleting}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={deleting}
                        data-testid="confirm-delete-pr"
                      >
                        {deleting ? "Deleting…" : "Delete PR"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}
            {canSubmit && (
              <div className="mt-3">
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60 transition"
                >
                  {submitting ? "Submitting…" : "Submit for Approval"}
                </button>
                {submitError && (
                  <p className="text-xs text-destructive mt-1 text-left">{submitError}</p>
                )}
              </div>
            )}
            {canCreateRfq && (
              <div className="mt-3">
                <Button asChild>
                  <Link to="/rfq/new" search={{ prId: detail.id }}>
                    <Send className="h-3.5 w-3.5" /> Create RFQ
                  </Link>
                </Button>
              </div>
            )}
            {canGeneratePo && !genPoOpen && (
              <button
                type="button"
                onClick={openGenPo}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition"
              >
                <ShoppingCart className="h-3.5 w-3.5" /> Generate PO
              </button>
            )}
            {canClosePr && (
              <div className="mt-3">
                <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="close-pr-button">
                      <XCircle className="h-3.5 w-3.5" /> Close PR
                    </Button>
                  </DialogTrigger>
                  <DialogContent dismissable={false}>
                    <DialogHeader>
                      <DialogTitle>Close this purchase request?</DialogTitle>
                      <DialogDescription>
                        This cancels all remaining (un-ordered) quantity on this request without
                        raising an RFQ. Any quantity already consumed onto an RFQ is unaffected. The
                        request will settle to Fully Consumed.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-1.5">
                      <label htmlFor="close-reason" className="text-xs font-medium text-foreground">
                        Reason (optional)
                      </label>
                      <textarea
                        id="close-reason"
                        value={closeReason}
                        onChange={(e) => setCloseReason(e.target.value)}
                        rows={3}
                        placeholder="Why is this request being closed?"
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30 resize-none"
                      />
                      {closeError && <p className="text-xs text-destructive">{closeError}</p>}
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setCloseOpen(false)}
                        disabled={closing}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleClosePr}
                        disabled={closing}
                        data-testid="confirm-close-pr-button"
                      >
                        {closing ? "Closing…" : "Confirm close"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </div>
        </div>

        {/* US-023 generate-PO panel — vendor picker. */}
        {canGeneratePo && genPoOpen && (
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 space-y-2">
            <p className="text-xs font-medium text-foreground">Generate a purchase order</p>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-muted-foreground">Vendor</span>
              <select
                aria-label="Vendor"
                value={genPoVendorId}
                onChange={(e) => setGenPoVendorId(e.target.value)}
                className="rounded-lg border border-border bg-background px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="">Choose a vendor…</option>
                {genPoVendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.company_name}
                  </option>
                ))}
              </select>
            </label>
            {genPoError && <p className="text-xs text-destructive">{genPoError}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={submitGenPo}
                disabled={genPoLoading || !genPoVendorId}
                className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition"
              >
                {genPoLoading ? "Creating…" : "Create PO"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setGenPoOpen(false);
                  setGenPoVendorId("");
                  setGenPoError(null);
                }}
                disabled={genPoLoading}
                className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Justification */}
        <div className="rounded-lg bg-muted/50 border border-border px-4 py-3">
          <p className="text-xs font-medium text-muted-foreground mb-1">Justification</p>
          <p className="text-sm text-foreground leading-relaxed">{detail.justification}</p>
        </div>
      </div>

      {/* Line items */}
      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-semibold">Line Items</h2>
        </div>
        <LineItemsTable items={detail.line_items} totalAmount={detail.total_amount} />
      </div>

      {/* Timeline + Approval steps */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Status timeline (3/5 width on large screens) */}
        <div className="lg:col-span-3 rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-semibold">Status Timeline</h2>
          </div>
          <div className="px-5 py-4">
            {detail.status_timeline.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No timeline events.</p>
            ) : (
              <ol className="space-y-0">
                {detail.status_timeline.map((item, idx) => {
                  const { icon } = timelineStyle(item.action_type);
                  const isLast = idx === detail.status_timeline.length - 1;
                  const label = TIMELINE_LABELS[item.action_type] ?? item.action_type;
                  return (
                    <li key={idx} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="mt-0.5">{icon}</div>
                        {!isLast && <div className="w-px flex-1 bg-border mt-1.5 mb-1.5" />}
                      </div>
                      <div className={`pb-4 min-w-0 ${isLast ? "" : ""}`}>
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                          <span className="text-sm font-medium">{label}</span>
                          {item.actor && (
                            <span className="text-xs text-muted-foreground">{item.actor.name}</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {formatDateTime(item.timestamp)}
                        </div>
                        {item.comment && (
                          <div className="mt-1.5 rounded-md bg-muted/60 border border-border px-3 py-2 text-xs text-foreground italic">
                            "{item.comment}"
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>

        {/* Approval steps (2/5 width) */}
        <div className="lg:col-span-2 rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-semibold">Approval Chain</h2>
          </div>
          <div className="px-5 py-4">
            {detail.approval_steps.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No approval steps.</p>
            ) : (
              <ol className="space-y-0">
                {detail.approval_steps.map((step, idx) => {
                  const isCurrent = step.status === "Pending" && step.level === firstPendingLevel;
                  const isLast = idx === detail.approval_steps.length - 1;
                  return (
                    <li key={idx} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="mt-0.5">
                          <StepIcon status={step.status} isCurrent={isCurrent} />
                        </div>
                        {!isLast && <div className="w-px flex-1 bg-border mt-1.5 mb-1.5" />}
                      </div>
                      <div className="pb-4 min-w-0">
                        <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">
                          Level {step.level}
                        </div>
                        <div className="text-sm font-medium">{step.assigned_to.name}</div>
                        {step.delegated_from && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            delegated from {step.delegated_from.name}
                          </div>
                        )}
                        <div
                          className={`text-xs mt-1 font-medium ${
                            step.status === "Approved"
                              ? "[color:var(--success)]"
                              : step.status === "Rejected"
                                ? "text-destructive"
                                : step.status === "ChangesRequested"
                                  ? "[color:var(--warning)]"
                                  : isCurrent
                                    ? "text-primary"
                                    : "text-muted-foreground"
                          }`}
                        >
                          {stepStatusLabel(step.status, isCurrent)}
                          {step.acted_at && (
                            <span className="font-normal text-muted-foreground ml-1.5">
                              · {formatDate(step.acted_at)}
                            </span>
                          )}
                        </div>
                        {step.comment && (
                          <div className="mt-1.5 rounded-md bg-muted/60 border border-border px-3 py-2 text-xs text-foreground italic">
                            "{step.comment}"
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}

            {/* Approver action panel */}
            {canAct && !pendingAction && (
              <div className="pt-3 border-t border-border space-y-2">
                <p className="text-xs text-muted-foreground font-medium">Your action required</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setPendingAction({ stepId: activeStep!.id, kind: "approve" });
                      setActionComment("");
                      setActionError(null);
                    }}
                    className="rounded-lg bg-[var(--success)] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 transition"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => {
                      setPendingAction({ stepId: activeStep!.id, kind: "request-changes" });
                      setActionComment("");
                      setActionError(null);
                    }}
                    className="rounded-lg border border-warning/40 [color:var(--warning)] px-3 py-1.5 text-xs font-medium hover:bg-warning/10 transition"
                  >
                    Request Changes
                  </button>
                  <button
                    onClick={() => {
                      setPendingAction({ stepId: activeStep!.id, kind: "reject" });
                      setActionComment("");
                      setActionError(null);
                    }}
                    className="rounded-lg border border-destructive/40 text-destructive px-3 py-1.5 text-xs font-medium hover:bg-destructive/10 transition"
                  >
                    Reject
                  </button>
                </div>
              </div>
            )}
            {canAct && pendingAction && (
              <div className="pt-3 border-t border-border space-y-2">
                <p className="text-xs font-medium text-foreground">
                  {ACTION_LABELS[pendingAction.kind]}
                </p>
                <textarea
                  value={actionComment}
                  onChange={(e) => setActionComment(e.target.value)}
                  placeholder={
                    pendingAction.kind === "approve" ? "Comment (optional)" : "Comment (required)"
                  }
                  rows={3}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30 resize-none"
                />
                {actionError && <p className="text-xs text-destructive">{actionError}</p>}
                <div className="flex gap-2">
                  <button
                    onClick={handleAction}
                    disabled={
                      actionLoading || (pendingAction.kind !== "approve" && !actionComment.trim())
                    }
                    className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition"
                  >
                    {actionLoading ? "Saving…" : "Confirm"}
                  </button>
                  <button
                    onClick={() => {
                      setPendingAction(null);
                      setActionComment("");
                      setActionError(null);
                    }}
                    disabled={actionLoading}
                    className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Attachments ownerType="purchase-requests" ownerId={id} />
    </div>
  );
}
